import React, { useEffect } from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Card } from "../components/ui/card";
import { SectionHeading } from "../components/Shared";
import ConsultationForm from "../components/ConsultationForm";
import { company } from "../mock/mock";

const Contact = () => {
  useEffect(() => {
    document.title = "Contact Us | Morrisville Communications";
  }, []);

  const info = [
    { icon: MapPin, label: "Visit us", value: company.address, href: "https://maps.google.com/?q=8045+Arco+Corporate+Dr+Raleigh+NC+27617" },
    { icon: Phone, label: "Call us", value: company.phone, href: `tel:${company.phoneTel}` },
    { icon: Mail, label: "Email us", value: company.email, href: `mailto:${company.email}` },
    { icon: Clock, label: "Support", value: "24/7 customer support", href: null },
  ];

  return (
    <div>
      <section className="brand-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-300">Contact us now!</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-4 max-w-3xl leading-tight">
            Get your no-cost consultation
          </h1>
          <p className="mt-5 text-xl text-blue-100 max-w-2xl">
            Fill in your requirements and a good time to call. One of our advisors will reach out with more details.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <SectionHeading eyebrow={company.legalName} title="We're here to help you thrive" />
            <p className="text-slate-600">
              Our team of experts is dedicated to providing the best possible service - from experienced technicians to friendly customer service representatives, we are committed to helping your business thrive.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {info.map((it) => {
                const I = it.icon;
                const inner = (
                  <Card className="card-hover p-5 rounded-2xl border-slate-100 h-full">
                    <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                      <I className="h-5 w-5 text-blue-700" />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{it.label}</div>
                    <div className="text-slate-800 font-medium mt-1 text-sm">{it.value}</div>
                  </Card>
                );
                return it.href ? (
                  <a key={it.label} href={it.href} target="_blank" rel="noreferrer">{inner}</a>
                ) : (
                  <div key={it.label}>{inner}</div>
                );
              })}
            </div>
            <a
              href={`mailto:${company.salesEmail}?subject=Business%20VoIP%20Phone%20System%20Consultation`}
              className="inline-flex items-center gap-2 font-semibold text-blue-700 hover:gap-3 transition-all"
            >
              Prefer email? Reach our Sales Engineer <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="lg:col-span-3">
            <Card className="p-8 rounded-3xl shadow-xl border-slate-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Request a call back</h3>
              <p className="text-slate-500 text-sm mb-6">No cost & no obligation.</p>
              <ConsultationForm />
            </Card>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden shadow-lg border border-slate-100">
            <iframe
              title="Morrisville Communications location"
              src="https://www.google.com/maps?q=8045+Arco+Corporate+Dr,+Raleigh,+NC+27617&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
